
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {Linkedin, Twitter, Mail, CheckCircle} from 'lucide-react'
import toast from 'react-hot-toast'

const Footer = () => {
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const subscribed = localStorage.getItem('nh_news_sub')
    if (subscribed === '1') {
      setIsSubscribed(true)
    }
  }, [])

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address')
      return
    }

    setIsSubmitting(true)
    try {
      // Simulate API call - replace with actual endpoint later
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      localStorage.setItem('nh_news_sub', '1')
      setIsSubscribed(true)
      toast.success('You\'re subscribed! 🎉')
      setEmail('')
    } catch (error) {
      toast.error('Could not subscribe right now. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <footer className="bg-[#0b2840] text-white relative overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        {/* Main Content Grid */}
        <div className="flex flex-col lg:flex-row justify-between gap-6 lg:gap-12">
          
          {/* Left Column - Brand, Tagline, Social & Copyright */}
          <div className="text-left space-y-3">
            {/* Brand - Logo + Text */}
            <Link 
              to="/" 
              className="inline-flex items-center gap-3 group mb-2"
            >
              <img 
                src="https://i.imghippo.com/files/KBd1355nw.png" 
                alt="Nursing Hive logo"
                className="w-10 h-auto object-contain transition-all group-hover:scale-105"
                style={{
                  background: 'transparent',
                  borderRadius: '0',
                  boxShadow: 'none'
                }}
              />
              <span className="text-lg font-bold text-white tracking-tight group-hover:text-[#12b2a6] transition-colors">
                Nursing Hive
              </span>
            </Link>
            
            {/* Tagline */}
            <p className="text-white/85 text-[15px] leading-relaxed">
              Ethical international nursing mobility
            </p>
            
            {/* Social Links */}
            <div className="flex items-center space-x-3 pt-1">
              <a
                href="https://linkedin.com/company/nursinghive"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-[#12b2a6] transition-colors duration-200"
                aria-label="Follow Nursing Hive on LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="https://x.com/nursinghive"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-[#12b2a6] transition-colors duration-200"
                aria-label="Follow Nursing Hive on X (Twitter)"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="mailto:hello@nursinghive.com"
                className="text-white/80 hover:text-[#12b2a6] transition-colors duration-200"
                aria-label="Email Nursing Hive"
              >
                <Mail className="h-5 w-5" />
              </a>
            </div>

            {/* Copyright */}
            <p className="text-white/70 text-sm pt-3">
              © 2025 Nursing Hive — All rights reserved.
            </p>
          </div>

          {/* Right Columns - Grouped Navigation */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8 flex-1">
            {/* Explore Group */}
            <div>
              <h3 className="text-white font-semibold text-sm mb-3">Explore</h3>
              <nav className="space-y-2">
                <Link to="/" className="block text-white/75 hover:text-[#12b2a6] transition-colors text-sm">
                  Home
                </Link>
                <Link to="/about" className="block text-white/75 hover:text-[#12b2a6] transition-colors text-sm">
                  About
                </Link>
                <Link to="/countries" className="block text-white/75 hover:text-[#12b2a6] transition-colors text-sm">
                  Browse Destinations
                </Link>
                <Link to="/resources" className="block text-white/75 hover:text-[#12b2a6] transition-colors text-sm">
                  Search Guides
                </Link>
                <Link to="/contact" className="block text-white/75 hover:text-[#12b2a6] transition-colors text-sm">
                  Contact
                </Link>
              </nav>
            </div>

            {/* Partners Group */}
            <div>
              <h3 className="text-white font-semibold text-sm mb-3">Partners</h3>
              <nav className="space-y-2">
                <Link to="/recruiters" className="block text-white/75 hover:text-[#12b2a6] transition-colors text-sm">
                  Recruiters <span className="text-xs text-white/50">🔜 Mar 1</span>
                </Link>
                <Link to="/affiliates" className="block text-white/75 hover:text-[#12b2a6] transition-colors text-sm">
                  Affiliates <span className="text-xs text-white/50">🔜 Mar 1</span>
                </Link>
              </nav>
            </div>

            {/* Legal Group */}
            <div>
              <h3 className="text-white font-semibold text-sm mb-3">Legal</h3>
              <nav className="space-y-2">
                <Link to="/faqs" className="block text-white/75 hover:text-[#12b2a6] transition-colors text-sm">
                  FAQ
                </Link>
                <Link to="/privacy" className="block text-white/75 hover:text-[#12b2a6] transition-colors text-sm">
                  Privacy
                </Link>
                <Link to="/disclaimer" className="block text-white/75 hover:text-[#12b2a6] transition-colors text-sm">
                  Disclaimer
                </Link>
              </nav>
            </div>
          </div>
        </div>

        {/* Newsletter Section - Horizontal Compact */}
        <div className="mt-6 pt-4 border-t border-white/10">
          {!isSubscribed ? (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <span className="text-white font-medium text-sm whitespace-nowrap">Stay Updated</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={isSubmitting}
                className="w-full sm:w-[240px] px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#12b2a6] disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-2 bg-[#12b2a6] text-white text-sm rounded-lg font-medium hover:bg-[#0fa093] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-[#12b2a6] flex-shrink-0" />
              <div>
                <span className="text-white font-semibold text-sm">Subscribed</span>
                <span className="text-white/60 text-xs ml-2">You'll receive updates at your email.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </footer>
  )
}

export default Footer
